import { z } from "zod";

const text = z.string().trim().min(1);
export const slugSchema = z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
export const assetSchema = z.string().regex(/^[a-zA-Z0-9][a-zA-Z0-9_-]*\.(png|jpe?g|webp|gif|avif)$/i);
export function isSafeHref(href: string): boolean {
  if (/^\/(?!\/)[^\s\\]*$/.test(href)) return true;
  try { const url = new URL(href); return url.protocol === "https:" && !url.username && !url.password; } catch { return false; }
}
const href = text.refine(isSafeHref, "Expected a safe internal path or HTTPS URL");
const isoDate = z.string().datetime({ offset: true });
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((s) => !Number.isNaN(Date.parse(s)) && new Date(s).toISOString().slice(0, 10) === s, "Invalid calendar date");
const image = z.object({ src: text, width: z.number().int().positive().max(20000), height: z.number().int().positive().max(20000) }).strict();
const table = z.object({ type: z.literal("table"), caption: text, headers: z.array(text).min(1), rows: z.array(z.array(text)).min(1) }).strict();
export const blockSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("p"), text }).strict(),
  z.object({ type: z.literal("h3"), text }).strict(),
  z.object({ type: z.literal("bullets"), items: z.array(text).min(1) }).strict(),
  z.object({ type: z.literal("steps"), items: z.array(text).min(1) }).strict(),
  table,
  image.extend({ type: z.literal("figure"), alt: text, caption: text }).strict(),
  z.object({ type: z.literal("callout"), tone: z.enum(["info", "warning"]), title: text, text }).strict(),
]);
const locale = z.object({
  metaTitle: text, metaDescription: text, keywords: text,
  category: text, date: text, readingTime: text, breadcrumb: text, eyebrow: text, h1: text,
  card: z.object({ title: text, subtitle: text }).strict(), coverAlt: text,
  intro: z.array(text).min(1), sections: z.array(z.object({ heading: text, blocks: z.array(blockSchema).min(1) }).strict()).min(1),
  faqTitle: text, faq: z.array(z.object({ q: text, a: text }).strict()),
  ctaTitle: text, ctaBody: text,
  relatedTitle: text, related: z.array(z.object({ label: text, href, desc: text }).strict()),
  sourcesTitle: text, sources: z.array(z.object({ label: text, publisher: text, url: href.refine((s) => s.startsWith("https://"), "Sources require HTTPS"), accessed: day }).strict()),
  factCheckLabel: text,
}).strict();
export const jsonPostSchema = z.object({
  schemaVersion: z.literal(1), slug: slugSchema,
  frPath: z.string().regex(/^\/blog\/[a-z0-9]+(?:-[a-z0-9]+)*$/),
  enPath: z.string().regex(/^\/en\/blog\/[a-z0-9]+(?:-[a-z0-9]+)*$/),
  datePublished: isoDate, dateModified: isoDate, lastFactCheck: day,
  cover: image, assets: z.array(assetSchema).min(1), fr: locale, en: locale,
}).strict().superRefine((post, ctx) => {
  const issue = (path: (string | number)[], message: string) => ctx.addIssue({ code: z.ZodIssueCode.custom, path, message });
  if (post.frPath !== `/blog/${post.slug}`) issue(["frPath"], "Must match the internal French slug");
  if (Date.parse(post.dateModified) < Date.parse(post.datePublished)) issue(["dateModified"], "Cannot precede datePublished");
  if (new Set(post.assets).size !== post.assets.length) issue(["assets"], "Duplicate asset filenames");
  const checkImage = (src: string, path: (string | number)[]) => {
    const prefix = `/images/blog/${post.slug}/`;
    if (!src.startsWith(prefix) || !post.assets.includes(src.slice(prefix.length))) issue(path, "Image must reference a listed asset in this post's public directory");
  };
  checkImage(post.cover.src, ["cover", "src"]);
  for (const lang of ["fr", "en"] as const) post[lang].sections.forEach((section, i) => section.blocks.forEach((block, j) => {
    if (block.type === "figure") checkImage(block.src, [lang, "sections", i, "blocks", j, "src"]);
    if (block.type === "table" && block.rows.some((row) => row.length !== block.headers.length)) issue([lang, "sections", i, "blocks", j, "rows"], "Every row must match the header count");
  }));
});
export type JsonPost = z.infer<typeof jsonPostSchema>;
export type JsonBlock = z.infer<typeof blockSchema>;
export type JsonPostLocale = JsonPost["fr"];
export function parseJsonPost(value: unknown, filename = "post.json"): JsonPost {
  const result = jsonPostSchema.safeParse(value);
  if (!result.success) throw new Error(`${filename}:\n${result.error.issues.map((i) => `  ${i.path.join(".")}: ${i.message}`).join("\n")}`);
  return result.data;
}
